const CANONICAL_DECIMAL_PATTERN = /^[+-]?\d+(?:\.\d+)?$/;
const GROUPED_DECIMAL_PATTERN = /^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/;
const UNSIGNED_CANONICAL_DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/;
const UNSIGNED_GROUPED_DECIMAL_PATTERN = /^\d{1,3}(?:,\d{3})+(?:\.\d+)?$/;
const ZERO_DECIMAL_PATTERN = /^0+(?:\.0+)?$/;

export interface PrescriptionDisplayFormatOptions {
  locale: string;
  showPlus?: boolean;
  emptyText?: string;
}

/**
 * Formats a canonical decimal value for display without changing its stored representation.
 *
 * Prescription and API values remain dot-decimal strings (for example, "+1.25").
 * German locales replace the decimal separator in the returned display text.
 * Values with standard comma thousands separators are localized as well
 * (for example, "123,234.123" becomes "123.234,123").
 * Non-numeric values such as "PL", "SPH/DS", "None", and "—" are preserved.
 */
export function formatPrescriptionDecimalForDisplay(
  value: string | number | null | undefined,
  locale: string,
): string {
  if (value === null || value === undefined) {
    return '';
  }

  const text = String(value);
  const normalizedLocale = locale.toLowerCase().replace('_', '-');

  if (!normalizedLocale.startsWith('de')) {
    return text;
  }

  if (CANONICAL_DECIMAL_PATTERN.test(text)) {
    return text.replace('.', ',');
  }

  if (!GROUPED_DECIMAL_PATTERN.test(text)) {
    return text;
  }

  const decimalSeparatorIndex = text.lastIndexOf('.');
  const integerPart = decimalSeparatorIndex === -1 ? text : text.slice(0, decimalSeparatorIndex);
  const fractionalPart = decimalSeparatorIndex === -1 ? '' : text.slice(decimalSeparatorIndex + 1);
  const localizedIntegerPart = integerPart.replace(/,/g, '.');

  return fractionalPart ? `${localizedIntegerPart},${fractionalPart}` : localizedIntegerPart;
}

/**
 * Formats one prescription field for display only.
 *
 * The input value is never parsed or written back. Callers opt into field-specific
 * presentation rules such as a leading plus sign or an empty-value placeholder.
 */
export function formatPrescriptionValueForDisplay(
  value: string | number | null | undefined,
  { locale, showPlus = false, emptyText = '' }: PrescriptionDisplayFormatOptions,
): string {
  if (value === null || value === undefined || String(value).trim() === '') {
    return emptyText;
  }

  const text = String(value);
  const isUnsignedDecimal =
    UNSIGNED_CANONICAL_DECIMAL_PATTERN.test(text) || UNSIGNED_GROUPED_DECIMAL_PATTERN.test(text);
  const isZero = ZERO_DECIMAL_PATTERN.test(text.replace(/,/g, ''));
  const displayText = showPlus && isUnsignedDecimal && !isZero ? `+${text}` : text;

  return formatPrescriptionDecimalForDisplay(displayText, locale);
}
